import type {
  ProductSetupConfig,
  SetupTask,
  SetupTaskGroup,
} from "../../registry/types";
import { ConciergeIntakeExperience } from "./concierge-intake/concierge-intake-experience";
import {
  CONCIERGE_INTAKE_HERO_IMAGE_URL,
  CONFIGURE_PLAYBOOK_TASK_ID,
  SENDING_NUMBER_TASK_ID,
} from "./constants";
import { SendingNumberStatusModal } from "./sending-number-status-modal";

const SENDING_NUMBER_TASK: SetupTask = {
  id: SENDING_NUMBER_TASK_ID,
  title: "Approving your sending number",
  description:
    "We'll keep checking the number that Commerce Agent uses to text customers. This usually takes a little time after setup.",
  route: (teamId) => `/stores/${teamId}/commerce-agent`,
  completionPredicate: (_team, progress) =>
    progress.intakeAnswers.phoneProvisioningStatus === "approved",
  requiredForLive: true,
  contextualModal: {
    title: "Sending number status",
    ctaText: "Close",
    renderBody: SendingNumberStatusModal,
  },
};

const CONFIGURE_PLAYBOOK_TASK: SetupTask = {
  id: CONFIGURE_PLAYBOOK_TASK_ID,
  title: "Review your Commerce Agent playbook",
  description:
    "See where Commerce Agent reaches out, how it speaks, and what it can handle for shoppers.",
  route: (teamId) => `/stores/${teamId}/commerce-agent/playbook`,
  completionPredicate: () => false,
  requiredForLive: true,
  completeOnRoute: /\/commerce-agent\/(playbook|settings)(\/|$)/,
};

const TASK_LIST = [SENDING_NUMBER_TASK, CONFIGURE_PLAYBOOK_TASK];

const GROUPS: SetupTaskGroup[] = [
  { id: "setup", title: "Setup", taskIds: TASK_LIST.map((task) => task.id) },
];

export const shopperAgentProductConfig: ProductSetupConfig = {
  product: "shopperAgent",
  displayName: "Commerce Agent",
  routePattern: /\/commerce-agent(\/|$)/,
  resumeRoute: (storeId) => `/stores/${storeId}/commerce-agent`,
  // No `onIntakeSubmit` yet — Commerce Agent has no post-intake activation
  // step today. Intake just persists answers; the welcome overlay does
  // not fire (matches existing host behavior pre-refactor).
  intake: {
    questions: [],
    welcome: {
      title: "Welcome to Commerce Agent",
      subtitle: "Let's get your Commerce Agent live.",
    },
    dismissible: false,
    heroImageUrl: CONCIERGE_INTAKE_HERO_IMAGE_URL,
    renderExperience: ConciergeIntakeExperience,
    showProgress: true,
  },
  groups: GROUPS,
  tasks: Object.fromEntries(TASK_LIST.map((task) => [task.id, task])),
  asyncTaskStatus: {
    pollIntervalMs: 30_000,
    load: async ({ rpcClient, progress, team }) => {
      const readiness = await rpcClient.getConciergePhoneReadiness({});
      const hasIMessage =
        team.settings?.shopperAgentAdmin?.allowIMessage === true;
      if (readiness.provisioningStatus === "approved") {
        return {
          [SENDING_NUMBER_TASK.id]: {
            complete: true,
            description:
              "Your sending number is approved and Commerce Agent can message customers.",
            variant: "complete",
          },
        };
      }
      if (readiness.provisioningStatus === "rejected") {
        return {
          [SENDING_NUMBER_TASK.id]: {
            complete: false,
            description:
              readiness.phoneNumberLastError ??
              "Your sending number needs attention before Commerce Agent can go live.",
            variant: "needs_attention",
          },
        };
      }
      if (
        readiness.provisioningStatus === "pending" ||
        progress.intakeAnswers.phoneProvisioningStatus === "pending"
      ) {
        return {
          [SENDING_NUMBER_TASK.id]: {
            complete: false,
            description: hasIMessage
              ? "Commerce Agent can message iPhone shoppers now. Your SMS number is being reviewed so it can reach everyone else too."
              : "Your SMS sending number is being reviewed. Commerce Agent will go live automatically once it is approved.",
            variant: "pending",
          },
        };
      }
      return {
        [SENDING_NUMBER_TASK.id]: {
          complete: false,
          description:
            "Finish the sending-number step so Commerce Agent can text customers.",
          variant: "not_started",
        },
      };
    },
  },
  completion: {
    title: "Commerce Agent is ready",
    body: "Commerce Agent is ready to keep building customer relationships.",
    ctaText: "Got it",
  },
  // Keep the memo key scoped to the settings + flag state this product reads.
  relevantTeamSlice: (team) => ({
    shopperAgentEnabled: team.shopperAgentEnabled,
    shopperAgentAdmin: {
      allowIMessage: team.settings?.shopperAgentAdmin?.allowIMessage,
    },
  }),
};
